/**
 * Dynamic Collection List Filter
 * Filters collections by month and search query
 */
class DynamicCollectionList {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.grid = null;
    this.items = [];
    this.activeMonth = 'all';
    this.searchQuery = '';
    
    // Month name mapping for normalization
    this.monthMap = {
      'january': 'january', 'jan': 'january', '1': 'january', '01': 'january',
      'february': 'february', 'feb': 'february', '2': 'february', '02': 'february',
      'march': 'march', 'mar': 'march', '3': 'march', '03': 'march',
      'april': 'april', 'apr': 'april', '4': 'april', '04': 'april',
      'may': 'may', '5': 'may', '05': 'may',
      'june': 'june', 'jun': 'june', '6': 'june', '06': 'june',
      'july': 'july', 'jul': 'july', '7': 'july', '07': 'july',
      'august': 'august', 'aug': 'august', '8': 'august', '08': 'august',
      'september': 'september', 'sep': 'september', 'sept': 'september', '9': 'september', '09': 'september',
      'october': 'october', 'oct': 'october', '10': 'october',
      'november': 'november', 'nov': 'november', '11': 'november',
      'december': 'december', 'dec': 'december', '12': 'december'
    };
    
    this.init();
  }
  
  normalizeMonth(month) {
    if (!month) return '';
    const normalized = month.toLowerCase().trim();
    return this.monthMap[normalized] || normalized;
  }

  init() {
    if (!this.sectionId) return;

    this.grid = document.getElementById(`dynamic-collection-grid-${this.sectionId}`);
    if (!this.grid) {
      console.warn('Dynamic Collection List: Grid not found', `dynamic-collection-grid-${this.sectionId}`);
      return;
    }

    this.items = Array.from(this.grid.querySelectorAll('.dynamic-collection-list__item'));
    
    if (this.items.length === 0) {
      console.warn('Dynamic Collection List: No items found');
      return;
    }
    
    // Show all items initially (they are already filtered by server-side subtype filter)
    this.items.forEach(item => {
      item.style.display = '';
    });
    
    // Initialize month filters
    this.initMonthFilters();
    
    // Initialize search
    this.initSearch();
    
    // Filter items (will show all if no month/search filter is active)
    this.filter();
    
    // Update month counts
    this.updateMonthCounts();
  }

  initMonthFilters() {
    const filterButtons = this.container.querySelectorAll('.dynamic-collection-list__filter-btn');
    
    filterButtons.forEach(button => {
      button.addEventListener('click', () => {
        // Remove active class from all buttons
        filterButtons.forEach(btn => btn.classList.remove('active'));
        
        // Add active class to clicked button
        button.classList.add('active');
        
        // Set active month (normalize to lowercase)
        const monthValue = button.dataset.month || 'all';
        this.activeMonth = monthValue.toLowerCase();
        
        // Filter items
        this.filter();
      });
    });
  }

  initSearch() {
    const searchInput = this.container.querySelector('.dynamic-collection-list__search-input');
    const clearButton = this.container.querySelector('.dynamic-collection-list__search-clear');
    
    if (!searchInput) {
      console.warn('Dynamic Collection List: Search input not found');
      return;
    }

    // Initialize search query
    this.searchQuery = '';

    // Debounce search input
    let searchTimeout;
    searchInput.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        const query = e.target.value.trim();
        this.searchQuery = query.toLowerCase();
        
        // Show/hide clear button
        if (clearButton) {
          clearButton.style.display = query ? 'block' : 'none';
        }
        
        // Filter items
        this.filter();
      }, 300);
    });

    // Also trigger on paste
    searchInput.addEventListener('paste', () => {
      setTimeout(() => {
        const query = searchInput.value.trim();
        this.searchQuery = query.toLowerCase();
        if (clearButton) {
          clearButton.style.display = query ? 'block' : 'none';
        }
        this.filter();
      }, 100);
    });

    // Clear search
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        searchInput.value = '';
        this.searchQuery = '';
        clearButton.style.display = 'none';
        this.filter();
        searchInput.focus();
      });
    }
  }

  filter() {
    let visibleCount = 0;
    const loadingEl = this.container.querySelector('.dynamic-collection-list__loading');
    const noResultsEl = this.container.querySelector('.dynamic-collection-list__no-results');

    if (loadingEl) loadingEl.style.display = 'block';
    if (noResultsEl) noResultsEl.style.display = 'none';

    // Use requestAnimationFrame for smooth filtering
    requestAnimationFrame(() => {
      this.items.forEach(item => {
        const monthRaw = item.dataset.collectionMonth || '';
        const month = this.normalizeMonth(monthRaw);
        const title = (item.dataset.collectionTitle || '').toLowerCase();
        const handle = (item.dataset.collectionHandle || '').toLowerCase();
        
        // Check month filter (normalize both values)
        const activeMonthNormalized = this.activeMonth === 'all' ? 'all' : this.normalizeMonth(this.activeMonth);
        const monthMatch = activeMonthNormalized === 'all' || month === activeMonthNormalized;
        
        // Check search query - search in title and handle
        let searchMatch = true;
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          // Search in title (unescaped) and handle
          const titleUnescaped = title.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
          searchMatch = titleUnescaped.includes(query) || title.includes(query) || handle.includes(query);
        }
        
        // Show/hide item
        if (monthMatch && searchMatch) {
          item.style.display = '';
          visibleCount++;
        } else {
          item.style.display = 'none';
        }
      });

      // Update UI
      if (loadingEl) loadingEl.style.display = 'none';
      
      if (visibleCount === 0) {
        if (noResultsEl) noResultsEl.style.display = 'block';
      } else {
        if (noResultsEl) noResultsEl.style.display = 'none';
      }

      // Update debug info
      this.updateDebugInfo(visibleCount);
    });
  }

  updateMonthCounts() {
    const monthButtons = this.container.querySelectorAll('.dynamic-collection-list__filter-btn[data-month]');
    
    monthButtons.forEach(button => {
      const month = (button.dataset.month || '').toLowerCase();
      if (month === 'all') return;

      let count = 0;
      const normalizedMonth = this.normalizeMonth(month);
      
      this.items.forEach(item => {
        const itemMonthRaw = item.dataset.collectionMonth || '';
        const itemMonth = this.normalizeMonth(itemMonthRaw);
        
        // Compare normalized months
        if (itemMonth && itemMonth === normalizedMonth) {
          count++;
        }
      });

      const countSpan = button.querySelector('.month-count');
      if (countSpan) {
        countSpan.textContent = count;
      }
      
      // Update button text - preserve original month name
      let originalMonthName = button.getAttribute('data-original-month');
      if (!originalMonthName) {
        originalMonthName = button.textContent.split(' (')[0];
        button.setAttribute('data-original-month', originalMonthName);
      }
      button.textContent = `${originalMonthName} (${count})`;
      
      // Hide button if count is 0
      if (count === 0) {
        button.style.display = 'none';
      } else {
        button.style.display = '';
      }
    });
  }

  updateDebugInfo(filteredCount) {
    const totalCountEl = document.getElementById('debug-total-count');
    const filteredCountEl = document.getElementById('debug-filtered-count');
    
    if (totalCountEl) {
      totalCountEl.textContent = this.items.length;
    }
    
    if (filteredCountEl) {
      filteredCountEl.textContent = filteredCount;
    }
  }
}

// Register custom element
if (!customElements.get('dynamic-collection-list')) {
  customElements.define('dynamic-collection-list', DynamicCollectionList);
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  const filters = document.querySelectorAll('.dynamic-collection-list__filters');
  filters.forEach(filter => {
    if (!filter.dataset.initialized) {
      filter.dataset.initialized = 'true';
      // The custom element will initialize itself
    }
  });
});

// Also initialize immediately if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDynamicCollectionLists);
} else {
  initDynamicCollectionLists();
}

function initDynamicCollectionLists() {
  const filters = document.querySelectorAll('.dynamic-collection-list__filters');
  filters.forEach(filter => {
    if (!filter.dataset.initialized) {
      filter.dataset.initialized = 'true';
      try {
        // Create instance
        new DynamicCollectionList(filter);
      } catch (error) {
        console.error('Dynamic Collection List: Error initializing', error);
      }
    }
  });
}
